import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import axios from 'axios';
import useDebounce from './useDebounce';

export const useUserDocuments = ({ orderId, autoFilterUsingDebounce = false }) => {
  const dataUser = useSelector((state) => state?.auth?.userDataDetail);

  const FEFiltersInitialState = {
    name: '',
    type: '',
  };
  const [FEFilters, setFEFilters] = useState(FEFiltersInitialState);

  // Imposta automaticamente la data odierna come endDate e un mese precedente come startDate
  const getInitialDates = () => {
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

    return {
      startDate: oneMonthAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const filtersInitialState = {
    orderId: orderId || '',
    ...getInitialDates(),
  };
  const [filters, setFilters] = useState(filtersInitialState);

  const [isLoading, setIsLoading] = useState(false);
  const [errorWhileLoading, setErrorWhileLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const debouncedFilters = useDebounce(filters, 300);

  const resetFilter = () => {
    if (filters.startDate || filters.endDate) {
      setFilters(filtersInitialState);
    }

    setFEFilters(FEFiltersInitialState);

    if (!autoFilterUsingDebounce) {
      fetchWithFilter(filtersInitialState);
    }
  };

  const updateFilter = (filter, value) => {
    setFilters((prevState) => ({
      ...prevState,
      [filter]: value,
    }));
  };

  const updateFEFilter = (filter, value) => {
    setFEFilters((prevState) => ({
      ...prevState,
      [filter]: value,
    }));
  };

  const fetchWithFilter = (filtersToUse = null) => {
    const { orderId, startDate, endDate } = filtersToUse ? filtersToUse : filters;
    const abortController = new AbortController();
    const signal = abortController.signal;

    setIsLoading(true);
    setErrorWhileLoading(false);

    axios
      .get('/v1/get-customer-documents', {
        params: {
          ...(orderId ? { orderid: orderId } : { customerid: dataUser.ACCOUNTNUM }),
          fromdate: startDate,
          todate: endDate,
        },
        headers: {
          Authorization: `Bearer ${dataUser?.token}`,
        },
        signal,
      })
      .then((res) => {
        setRows([
          ...(res?.data?.result?.data?.listOfPDFInvoices || []),
          ...(res?.data?.result?.data?.listOfPDFPackingSlips || []),
          ...(res?.data?.result?.data?.listOfPDFtc || []),
        ]);
      })
      .catch((error) => {
        console.error('error', error);
        setRows([]);
        setErrorWhileLoading(true);
      })
      .finally(() => setIsLoading(false));

    return () => abortController.abort();
  };

  // FirstRender
  useEffect(fetchWithFilter, []);

  // AutoFiltering
  useEffect(() => {
    if (!autoFilterUsingDebounce) {
      return;
    }

    return fetchWithFilter();
  }, [debouncedFilters, autoFilterUsingDebounce]);

  // FilterOnClick
  const handleFilterClick = () => fetchWithFilter();

  const filteredRows = useMemo(() => {
    return rows
      // Escludi sempre i TC dalla visualizzazione come elementi separati
      .filter((item) => item?.document_type !== 'listOfPDFtc')
      .filter((item) => {
        if (!FEFilters.name.length) {
          return true;
        }

        return item?.name?.toLowerCase()?.includes(FEFilters.name.toLowerCase());
      })
      .filter((item) => {
        if (!FEFilters.type.length) {
          return true;
        }

        // Se il filtro è per TC, mostra solo le fatture che hanno un TC associato
        if (FEFilters.type === 'listOfPDFtc') {
          if (item?.document_type === 'listOfPDFInvoices') {
            const baseName = item.name?.endsWith('.pdf') 
              ? item.name.replace('.pdf', '') 
              : item.name;
            const possibleTCNames = [
              `${baseName}_TC.pdf`,
              `${baseName}_TC`,
              `${item.name}_TC.pdf`,
              `${item.name}_TC`,
            ];
            const hasTC = rows.some(doc => doc.document_type === 'listOfPDFtc' && possibleTCNames.includes(doc.name));
          }
          return false;
        }

        // Filtra direttamente per tipo di documento
        return item?.document_type === FEFilters.type;
      });
  }, [rows, FEFilters]);

  return {
    isLoading,
    errorWhileLoading,
    rows,
    filteredRows,
    filters,
    updateFilter,
    FEFilters,
    updateFEFilter,
    resetFilter,
    handleFilterClick,
  };
};